import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShieldCheck, Ticket, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { WebinarBonus } from "@/components/WebinarBonus";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { ContactLink } from "@/components/ContactDialog";
import { TopLogoBar } from "@/components/AppShell";
import { track } from "@/lib/track";
import { getPromo, clearPromo } from "@/lib/promo";

export default function Paywall() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [selected, setSelected] = useState(() => (localStorage.getItem("a2g_plan") === "monthly" ? "a2g_monthly" : "a2g_annual"));
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(getPromo);
  const [promo, setPromo] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (user?.subscription_active) {
      navigate(user?.profile_id ? "/oggi" : "/test", { replace: true });
      return;
    }
    const stored = (() => {
      try {
        return JSON.parse(localStorage.getItem("a2g_result") || "null");
      } catch {
        return null;
      }
    })();
    const hasProfile = user?.assessment?.profile_id || user?.profile_id;
    if (!hasProfile && stored?.profile_id) {
      api.post("/me/result", { profile_id: stored.profile_id, scores: stored.scores }).then(refreshUser).catch(() => {});
    } else if (!hasProfile && !stored?.profile_id) {
      navigate("/test", { replace: true });
      return;
    }
    const loadPlans = (n = 0) =>
      api.get("/payments/plans").then(({ data }) => setPlans(data)).catch((e) => {
        if (n < 4) setTimeout(() => loadPlans(n + 1), 3000);
        else toast.error(apiError(e.response?.data?.detail));
      });
    loadPlans();
  }, []); // eslint-disable-line

  const checkout = async () => {
    setLoading(true);
    const inIframe = window.self !== window.top;
    const win = inIframe ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: selected, origin_url: window.location.origin, promo_code: promo?.code });
      if (win) win.location.href = data.checkout_url;
      else window.location.href = data.checkout_url;
    } catch (e) {
      if (win) win.close();
      toast.error(apiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/payments/redeem", { code, lookup_key: selected });
      clearPromo();
      if (data.type === "free") {
        track("trial_start");
        await refreshUser();
        toast.success("Codice attivato: accesso completo gratuito!");
        navigate("/oggi", { replace: true });
        return;
      }
      setPromo(data);
      toast.success(`Codice ${data.code} applicato: ${data.label}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRedeeming(false);
    }
  };

  if (!plans) return <Loader />;

  return (
    <div className="min-h-screen px-5 pb-28 pt-6" data-testid="paywall-page">
      <TopLogoBar onBack={() => navigate(-1)} testid="paywall-back-button" />

      <div className="mt-8 text-center">
        <p className="eyebrow">7 giorni gratis, poi decidi tu</p>
        <h1 className="font-serif-el mt-2 text-3xl font-semibold text-[#FAF9F6]">Sblocca il tuo percorso</h1>
        <p className="mt-2 text-base text-[#B4B4BC]">Accesso completo ai 7 giorni, letture guidate, esercizio finale e serie di costanza.</p>
      </div>

      <div className="mt-7 grid grid-cols-2 gap-3" role="radiogroup">
        {[...plans.plans].sort((a, b) => (b.id === "annual") - (a.id === "annual")).map((p) => {
          const active = selected === p.lookup_key;
          return (
            <div
              key={p.lookup_key}
              role="radio"
              aria-checked={active}
              tabIndex={0}
              onClick={() => setSelected(p.lookup_key)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSelected(p.lookup_key)}
              data-testid={`plan-${p.id}`}
              className={`relative flex cursor-pointer flex-col rounded-3xl border-2 p-4 pt-5 text-left transition active:scale-[0.98] ${active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#27272A] bg-[#121215]"}`}
            >
              {p.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#E5C158] to-[#997A20] px-2.5 py-0.5 text-[11px] font-semibold text-[#09090B]">{p.savings}</span>
              )}
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold text-[#FAF9F6]">{p.id === "annual" ? "Annuale" : "Mensile"}</p>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full ${active ? "text-[#09090B]" : "border border-[#3F3F46]"}`} style={active ? { background: "linear-gradient(135deg,#E5C158,#997A20)" } : {}}>
                  {active && <Check size={12} />}
                </span>
              </div>
              <p className="font-serif-el mt-2 text-2xl font-semibold text-[#FAF9F6]">{p.price}<span className="text-sm font-normal text-[#8A8E96]">/{p.period}</span></p>
              <p className="mt-1 text-[13px] leading-snug text-[#B4B4BC]">{p.monthly_equivalent || p.billed}</p>
              <p className={`mt-2 text-[13px] font-medium ${p.id === "annual" ? "text-[#E5C158]" : "text-[#71717A]"}`}>{p.id === "annual" ? "+ 4 webinar (valore €80)" : "Senza webinar"}</p>
            </div>
          );
        })}
      </div>
      <WebinarBonus />

      <div className="mt-6 space-y-2.5 rounded-2xl border border-[#27272A] bg-[#121215] p-4">
        {["Percorso completo di 7 giorni", "3 momenti guidati al giorno", "Letture ed esercizio pratico", "Serie di costanza e riflessioni salvate"].map((f) => (
          <div key={f} className="flex items-center gap-2 text-base text-[#D4D4D8]"><Check size={16} className="text-[#D4AF37]" /> {f}</div>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-dashed border-[#D4AF37]/40 bg-[#121215] p-4" data-testid="promo-box">
        <p className="flex items-center gap-2 text-base font-semibold text-[#FAF9F6]"><Ticket size={18} className="text-[#D4AF37]" /> Hai un codice?</p>
        {promo ? (
          <div className="mt-3 flex items-center justify-between rounded-xl bg-[#D4AF37]/10 px-4 py-3" data-testid="promo-applied">
            <span className="text-sm text-[#E5C158]"><b>{promo.code}</b> · {promo.label}</span>
            <button onClick={() => setPromo(null)} data-testid="promo-remove-button" className="text-[#B4B4BC]"><X size={16} /></button>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && redeem()}
              placeholder="Inserisci il codice"
              data-testid="promo-code-input"
              className="min-w-0 flex-1 rounded-xl border border-[#27272A] bg-[#09090B] px-4 py-3 text-base uppercase tracking-wider text-[#FAF9F6] placeholder:normal-case placeholder:tracking-normal placeholder:text-[#8A8E96] focus:border-[#D4AF37]/60 focus:outline-none"
            />
            <button onClick={redeem} disabled={redeeming || !code.trim()} data-testid="promo-apply-button" className="btn-outline-gold rounded-xl px-4 text-sm font-semibold disabled:opacity-40">
              {redeeming ? "…" : "Applica"}
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start gap-2 text-sm text-[#8A8E96]">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#D4AF37]" />
        Nessun addebito per 7 giorni. Puoi annullare in qualsiasi momento con 1 clic.
      </div>
      <div className="mt-5 flex justify-center pb-28">
        <ContactLink testId="paywall-contact-link" />
      </div>

      <div className="fixed inset-x-0 bottom-0 mx-auto max-w-[430px] border-t border-[#27272A] bg-[#09090B]/95 p-4 backdrop-blur-md">
        <button onClick={checkout} disabled={loading} data-testid="checkout-button" className="btn-gold w-full rounded-full px-6 py-4 text-base uppercase tracking-wide">
          {loading ? "Ti porto al pagamento…" : "Inizia i 7 giorni gratis"}
        </button>
      </div>
    </div>
  );
}
